import { DatePickerView } from '@material-ui/pickers';
import { freqType } from '../../../hooks/useSip';
import {
    isAllowedMotilalWeeklyDay,
    isMotilalOswal,
    MOTILAL_FORTNIGHTLY_END_DATES,
} from '../../../hooks/motilalSipHelpers';
import {
    AmountType,
    defalutSipData,
    sipType,
    _schemeDetailsNewType,
} from '../../../redux-store/types/commercialTransactions';
import { DATE_VALUES, portfolioDetailsType } from './AdditionalPurchase/types';
import { getSipDays } from './ExploreMFs/mf-helpers';
import en from '../../../lang/en-us';

const {
    dbCommercial: { exploreMfs },
} = en;
type dayMappingType = {
    [key: string]: number;
};

const dayMappings: dayMappingType = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
};

// Ordered list of weekdays for range expansion (Monday=0 to Sunday=6 in this context for range iteration)
const ORDERED_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Check if a string is a day range pattern like "Monday To Friday"
const isDayRangePattern = (str: string): boolean => {
    const normalized = (str || '').toLowerCase();
    return /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+(to)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i.test(normalized);
};

// Parse a day range string like "Monday To Friday" and return array of day names
// Returns individual day names: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
export const expandDayRange = (rangeStr: string): Array<string> => {
    const normalized = (rangeStr || '').toLowerCase();
    const match = normalized.match(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+to\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i);
    if (!match) return [];

    const startDay = match[1].toUpperCase();
    const endDay = match[2].toUpperCase();

    const startIdx = ORDERED_DAYS.indexOf(startDay);
    const endIdx = ORDERED_DAYS.indexOf(endDay);

    if (startIdx === -1 || endIdx === -1) return [];

    const result: Array<string> = [];
    // Handle forward ranges (Monday to Friday)
    if (startIdx <= endIdx) {
        for (let i = startIdx; i <= endIdx; i++) {
            result.push(ORDERED_DAYS[i].charAt(0) + ORDERED_DAYS[i].slice(1).toLowerCase());
        }
    } else {
        // Handle wrap-around ranges (Friday to Monday) - go through the week
        for (let i = startIdx; i < ORDERED_DAYS.length; i++) {
            result.push(ORDERED_DAYS[i].charAt(0) + ORDERED_DAYS[i].slice(1).toLowerCase());
        }
        for (let i = 0; i <= endIdx; i++) {
            result.push(ORDERED_DAYS[i].charAt(0) + ORDERED_DAYS[i].slice(1).toLowerCase());
        }
    }
    return result;
};

// Check if the sipDays array contains day range patterns
export const hasDayRangePattern = (sipDays: Array<string>): boolean => {
    if (!Array.isArray(sipDays) || sipDays.length === 0) return false;
    return sipDays.some((day: string) => isDayRangePattern(day));
};

// day-of-week indices (0 = Sunday .. 6 = Saturday)
const WEEKDAY_INDICES = [1, 2, 3, 4, 5]; // Monday - Friday
const WEEKEND_INDICES = [0, 6]; // Sunday, Saturday

export const isMotilalSipStartDateDisabled = (
    day: Date | null,
    frequency: string | undefined,
    amc?: string,
    sipDay?: number,
): boolean => {
    if (!day || !isMotilalOswal(amc)) return false;
    if (frequency === freqType.W) {
        if (day.getDay() === 0 || day.getDay() === 6) return true;
        if (
            typeof sipDay === 'number' &&
            isAllowedMotilalWeeklyDay(sipDay) &&
            day.getDay() !== sipDay
        ) {
            return true;
        }
    }
    return false;
};

export const isMotilalSipEndDateDisabled = (
    day: Date | null,
    startDate: Date,
    frequency: string | undefined,
    amc?: string,
): boolean => {
    if (!day || !isMotilalOswal(amc)) return false;
    if (frequency === freqType.W && (day.getDay() === 0 || day.getDay() === 6)) {
        return true;
    }
    if (frequency === freqType.F) {
        const allowedEndDays = MOTILAL_FORTNIGHTLY_END_DATES[startDate.getDate()];
        if (!!allowedEndDays && !allowedEndDays.includes(day.getDate())) {
            return true;
        }
    }
    return false;
};

export const calculateInstallment = (
    installment: number,
    sipDetails: sipType,
    currentScheme: any,
): number => {
    if (!!sipDetails) {
        let availableInstallments = sipDetails[
            currentScheme.frequency as string
        ]?.minimumInstallments
            .map((currentInstallment) => currentInstallment.installment)
            .map(Number)
            .sort((a: number, b: number) => a - b);
        return availableInstallments && availableInstallments.length > 0
            ? availableInstallments.reduce(
                (acc: number, availableInstallment: number, index: number) => {
                    if (Math.trunc(Number(installment)) >= availableInstallment) return index;
                    return acc;
                },
                0,
            )
            : 0;
    }
    return 0;
};
// Parse a string-based sip-days array into the set of allowed day-of-week indices
// (0 = Sunday .. 6 = Saturday). Handles single/multiple named days and the
// "weekday"/"weekend" keyword groups, e.g.
//   ['every wednesday']          -> [3]
//   ['wednesday', 'friday']      -> [3, 5]
//   ['every week day']           -> [1, 2, 3, 4, 5]
//   ['every weekend']            -> [0, 6]
//   ['Monday To Friday']         -> [1, 2, 3, 4, 5]
// Returns [] when no weekday could be resolved (caller falls back to day-of-month).
export const getAllowedSipWeekdays = (sipDays: Array<string>): Array<number> => {
    if (isAllDaysArray(sipDays)) return [];
    const allowedDays = new Set<number>();
    sipDays.forEach((entry: string) => {
        const normalized = (entry || '').toLowerCase();

        // Handle day range patterns like "Monday To Friday"
        if (isDayRangePattern(entry)) {
            const expandedDays = expandDayRange(entry);
            expandedDays.forEach((dayName: string) => {
                const dayKey = dayName.toUpperCase();
                if (Object.prototype.hasOwnProperty.call(dayMappings, dayKey)) {
                    allowedDays.add(dayMappings[dayKey]);
                }
            });
            return;
        }

        if (normalized.includes('weekend')) {
            WEEKEND_INDICES.forEach((dayIndex) => allowedDays.add(dayIndex));
        }
        if (normalized.includes('weekday') || normalized.includes('week day')) {
            WEEKDAY_INDICES.forEach((dayIndex) => allowedDays.add(dayIndex));
        }
        normalized.split(' ').forEach((token: string) => {
            const dayKey = token.toUpperCase();
            if (Object.prototype.hasOwnProperty.call(dayMappings, dayKey)) {
                allowedDays.add(dayMappings[dayKey]);
            }
        });
    });
    return Array.from(allowedDays);
};

// For day-of-month sip arrays (e.g. ['1','8','15','22']) returns the allowed
// dates-of-month. Returns [] for weekday-named / keyword / 'All' arrays, which are
// handled by getAllowedSipWeekdays instead.
export const getAllowedSipMonthDays = (sipDays: Array<string>): Array<number> => {
    const isDayOfMonthArray =
        Array.isArray(sipDays) &&
        sipDays.length > 0 &&
        sipDays.every((day: string) => !isNaN(parseInt(day)));
    return isDayOfMonthArray ? getSipDays(sipDays) : [];
};

// Check if the sip days array is just ["All"] — meaning all days are allowed.
const isAllDaysArray = (sipDays: Array<string>): boolean =>
    Array.isArray(sipDays) &&
    sipDays.length === 1 &&
    sipDays[0].trim().toLowerCase() === 'all';


export const isWeekdayNamedArray = (sipDays: Array<string>): boolean => {
    if (!Array.isArray(sipDays) || sipDays.length === 0) return false;
    if (isAllDaysArray(sipDays)) return false;
    return sipDays.every((day: string) => isNaN(parseInt(day)));
};

// Format weekday-named array into a display string like "Every Wednesday" or "Every Wednesday, Friday"
// Also handles day range patterns: ['Monday To Friday'] -> "Every Monday, Tuesday, Wednesday, Thursday, Friday"
export const formatWeekdayDisplay = (sipDays: Array<string>): string => {
    if (!isWeekdayNamedArray(sipDays)) return '';
    const weekdays: string[] = [];

    sipDays.forEach((day: string) => {
        // Handle day range patterns like "Monday To Friday"
        if (isDayRangePattern(day)) {
            const expandedDays = expandDayRange(day);
            weekdays.push(...expandedDays);
        } else {
            const normalized = day.toLowerCase().replace(/every|alternate|week/g, '').trim();
            const formatted = normalized.charAt(0).toUpperCase() + normalized.slice(1);
            if (formatted.length > 0) {
                weekdays.push(formatted);
            }
        }
    });

    if (weekdays.length === 0) return '';
    return `Every ${weekdays.join(', ')}`;
};
// ['every wednesday', 'every friday'] ->  [{value: 'every wednesday', label: 'Every Wednesday'}, {value: 'every friday', label: 'Every Friday'}]
// ['Monday To Friday'] -> [{value: 'Monday', label: 'Monday'}, {value: 'Tuesday', label: 'Tuesday'}, ...]
export const getWeekdayDropdownOptions = (
    sipDays: Array<string>,
): Array<{ value: string; label: string }> => {
    if (!isWeekdayNamedArray(sipDays)) return [];

    const options: Array<{ value: string; label: string }> = [];

    sipDays
        .filter((day: string) => !!day)
        .forEach((day: string) => {
            // Handle day range patterns like "Monday To Friday"
            if (isDayRangePattern(day)) {
                const expandedDays = expandDayRange(day);
                expandedDays.forEach((expandedDay: string) => {
                    options.push({
                        value: expandedDay,
                        label: expandedDay,
                    });
                });
            } else {
                const label = day
                    .toLowerCase()
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                options.push({ value: day, label });
            }
        });

    return options;
};
export const startDateDisable = (
    day: Date | null,
    sipDetails: sipType | null,
    currentScheme: any,
    startDate: Date,
    stpFlag: boolean,
    kfinFlag: boolean,
    selectedWeekday?: string | null,
    amc?: string,
) => {
    if (!!day && !!sipDetails) {
        let sipDayList = selectedWeekday
            ? [selectedWeekday]
            : sipDetails[currentScheme?.frequency || 0]?.dates || [];
        // let sipDayList = ['Every tuesday', 'Alternate tuesday'];
        //check if every entry in the sipday list is a string [ex: ['wednesday', 'friday'] not ['1', '2',..]]]
        //if so, allow only that day of the week

        let allStringFlag = sipDayList.every((sipDay: string) => isNaN(parseInt(sipDay)));
        // when the sip days are named (e.g. ['every wednesday'], ['weekday'], ['weekend']),
        // restrict the calendar to the allowed days of the week.
        // Only for KFIN RTA + Weekly/Fortnightly frequencies.
        let isWeeklyOrFortnightly =
            currentScheme?.frequency === freqType.W || currentScheme?.frequency === freqType.F;
        let allowedSipWeekdays =
            kfinFlag && allStringFlag && isWeeklyOrFortnightly
                ? getAllowedSipWeekdays(sipDayList)
                : [];
        let hasWeekdayConstraint = allowedSipWeekdays.length > 0;
        if (hasWeekdayConstraint && !allowedSipWeekdays.includes(day.getDay())) {
            return true;
        }
        //disable days past the start date
        // if (day.getTime() < startDate.getTime()) {
        //     return true;
        // }
        //current sip days
        let maxDateFlag = false,
            minDateFlag = false;
        // disable start date after maxSipTat days
        const maxDate = new Date();
        if (kfinFlag && isWeeklyOrFortnightly) maxDate.setHours(23, 59, 59, 999);
        const minDate = new Date();
        if (kfinFlag && isWeeklyOrFortnightly) minDate.setHours(0, 0, 0, 0);
        let maxSipTat =
            sipDetails[currentScheme?.frequency || 0]?.maxSipTat || DATE_VALUES.startDateInterval;
        maxDate.setDate(maxDate.getDate() + parseInt(maxSipTat as string));
        let minSipTat = sipDetails[currentScheme?.frequency || 0]?.minSipTat || DEFAULT_MIN_TAT;
        minDate.setDate(minDate.getDate() + parseInt(minSipTat as string));
        if (day < minDate) minDateFlag = true;
        if (day > maxDate) maxDateFlag = true;
        //if frequency is weekly, enable monday date only.
        let mondayFlag = false;
        // if (currentScheme?.frequency === freqType.W) {
        //     if (day.getDay() !== 1) mondayFlag = true;
        // }
        // Disable weekends for Nippon (RMF) weekly STP
        if (amc === 'RMF' && currentScheme?.frequency === freqType.W) {
            if (day.getDay() === 0 || day.getDay() === 6) return true;
        }
        //enable the dates for only the days present in sip
        // if frequency is daily, disable saturday and sunday
        if (
            !stpFlag &&
            (currentScheme?.frequency === freqType.D || currentScheme?.frequency === freqType.DZ) &&
            kfinFlag
        ) {
            if (day.getDay() === 0 || day.getDay() === 6) return true;
        }
        let currentSipDays = getSipDays(sipDayList);
        // day-of-month restriction only applies to numeric sip-day arrays; named
        // (weekday) arrays are already constrained by hasWeekdayConstraint above.
        let dateOfMonthDisabled =
            !hasWeekdayConstraint && !currentSipDays.includes(day?.getDate() || 1);
        return dateOfMonthDisabled || maxDateFlag || mondayFlag || minDateFlag;
    }
    return false;
};

export const endDateDisable = (
    day: Date | null,
    sipDetails: sipType | null,
    currentScheme: any,
    startDate: Date,
    endDate: Date,
    stpFlag: boolean,
    kfinFlag: boolean,
    selectedWeekday?: string | null,
    amc?: string,
) => {
    if (!!day && !!sipDetails) {
        let sipDayList = selectedWeekday
            ? [selectedWeekday]
            : sipDetails[currentScheme?.frequency || 0]?.dates || [];
        // let sipDayList = ['Every wednesday', 'Alternate wednesday'];
        //check if every entry in the sipday list is a string [ex: ['wednesday', 'friday'] not ['1', '2',..]]]
        //if so, allow only that day of the week
        let allStringFlag = sipDayList.every((sipDay: string) => isNaN(parseInt(sipDay)));
        // when the sip days are named (e.g. ['every wednesday'], ['weekday'], ['weekend']),
        // restrict the calendar to the allowed days of the week.
        // applies to KFIN RTA + Weekly/Fortnightly frequencies.
        let isWeeklyOrFortnightly =
            currentScheme?.frequency === freqType.W || currentScheme?.frequency === freqType.F;
        let allowedSipWeekdays =
            kfinFlag && allStringFlag && isWeeklyOrFortnightly
                ? getAllowedSipWeekdays(sipDayList)
                : [];
        let hasWeekdayConstraint = allowedSipWeekdays.length > 0;
        if (hasWeekdayConstraint && !allowedSipWeekdays.includes(day.getDay())) {
            return true;
        }
        switch (currentScheme?.frequency) {
            case freqType.M:
                if (day.getDate() !== startDate.getDate()) return true;
                break;
            case freqType.W:
                // Disable weekends for Nippon (RMF)
                if (amc === 'RMF' && (day.getDay() === 0 || day.getDay() === 6)) return true;
                // Weekday-cadence arrays (named days like 'Wednesday') pin the end date
                // to the start weekday. Day-of-month arrays (e.g. [1,8,15,22]) are pinned
                // by date-of-month instead (enforced below), so skip the weekday match.
                // "All" means any day is valid — no weekday pinning.
                // Only applies to KFIN RTA.
                if (kfinFlag && allStringFlag && !isAllDaysArray(sipDayList) && day.getDay() !== startDate.getDay()) return true;
                break;
            case freqType.F:
                // Fortnightly logic only applies to KFIN RTA
                if (kfinFlag) {
                    if (amc === 'RMF' && (day.getDay() === 0 || day.getDay() === 6)) return true;
                    if (allStringFlag && !isAllDaysArray(sipDayList) && day.getDay() !== startDate.getDay()) return true;
                }
                break;
            case freqType.D:
            case freqType.DZ:
                if ((!stpFlag && day.getDay() === 0) || (day.getDay() === 6 && kfinFlag))
                    return true;
                break;
            default:
        }
        if (day.getFullYear() > 2099) return true;
        let currentSipDays = getSipDays(sipDayList);
        // day-of-month restriction only applies to numeric sip-day arrays; named
        // (weekday) arrays are already constrained by hasWeekdayConstraint above.
        return !hasWeekdayConstraint && !currentSipDays.includes(day?.getDate() || 1);
    } else return false;
};

export const handleCalendarView = (frequency: freqType): DatePickerView[] => {
    return [freqType.A].includes(frequency)
        ? ['year']
        : [freqType.H, freqType.Q, freqType.M].includes(frequency)
            ? ['month', 'year']
            : ['year', 'month', 'date'];
};

export const MAX_DATE = new Date('2099-12-31');

export const getLastWeekdayOfMonth = (year: number, month: number, weekday: number): Date | null => {
    if (year == null || month == null || weekday == null) return null;
    if (weekday < 0 || weekday > 6) return null;
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const lastDayWeekday = lastDayOfMonth.getDay();
    let daysToSubtract = lastDayWeekday - weekday;
    if (daysToSubtract < 0) {
        daysToSubtract += 7;
    }
    const result = new Date(lastDayOfMonth);
    result.setDate(lastDayOfMonth.getDate() - daysToSubtract);
    return result;
};

export const getWeekdayIndex = (weekdayStr: string | null | undefined): number => {
    if (!weekdayStr) return -1;
    const normalized = weekdayStr.toLowerCase();
    for (const [dayName, index] of Object.entries(dayMappings)) {
        if (dayName && normalized.includes(dayName.toLowerCase())) {
            return index;
        }
    }
    return -1;
};

import * as yup from 'yup';
import { MAX_LENGTHS, MIN_LENGHTHS } from './Switch/types';
import { IsAllow, SCHEMES_RESPONSE } from '../../../redux-store/types/portfolio';
import {
    DEFAULT_MAX_INVESTMENT,
    DEFAULT_MIN_TAT,
    TRACK_STATUS_PATH,
} from '../../../utils/constants';
import { folioPurchaseEnum, PaymentTypes } from './ExploreMFs/types';
import { SwitchBy, Values } from './Switch/SwitchInvestment';
import { rtaNames, rtaType } from '../../common/FatcaCheck.types';

export const investModeValidationSchema = yup.object({
    distId: yup
        .string()
        .required(exploreMfs.errors.distId)
        .min(MIN_LENGHTHS.ARN_LENGTH, exploreMfs.errors.minLengthForArn),
    // subDist: yup
    //     .string()
    //     .required(exploreMfs.errors.subDist)
    //     .min(MIN_LENGHTHS.ARN_LENGTH, exploreMfs.errors.minLengthForArn),
    euin: yup
        .string()
        .required(exploreMfs.errors.EUINmandatory)
        .min(MAX_LENGTHS.EUIN_LENGTH, exploreMfs.errors.maxLengthForEuin),
    euinDeclarationFlag: yup.string().required(exploreMfs.errors.euinFlag),
    riaCode: yup.string().required(exploreMfs.errors.RIAMandatory),
});

export const formatRtaResponse = (data: any) => {
    const details: _schemeDetailsNewType = data.reduce((prev: any, curr: any, index: number) => {
        return index === 0 ? { ...prev, commonDetails: { ...curr } } : { ...prev, ...curr };
    }, {});
    return details;
};

export const isSipAllowed = (data: _schemeDetailsNewType['sip']) => {
    if (typeof data !== 'object') {
        return { bool: IsAllow.No, sip: defalutSipData };
    }
    const sip = Object.keys(data).reduce((prev: any, key) => {
        if (!Object.keys(data[key] as object).length) {
            return prev;
        }

        return {
            ...prev,
            [key]: {
                ...data[key],
                multiples:
                    !data[key]?.multiples || Number(data[key]?.multiples as any) < 1
                        ? 1
                        : data[key]?.multiples,
            },
        };
    }, {});
    return sip === null ? { bool: IsAllow.No, sip: defalutSipData } : { bool: IsAllow.YES, sip };
};

export const sortSip = (sip: any) => {
    if (!!sip && Object.keys(sip).length > 0) {
        Object.keys(sip).forEach((key) => {
            if (Object.keys(sip[key].minimumInstallments).length > 0) {
                sip[key].minimumInstallments = sip[key].minimumInstallments.sort(
                    (a: any, b: any) => a?.installment - b?.installment,
                );
            } else {
                throw new Error(en.dbCommercial.sip.errors.emptyInstallment);
            }
        });
    }
};

export const trackMyRequestPath = (id: string) => {
    return `${TRACK_STATUS_PATH}?tab=2`;
};

export const displayTransactionType = (type: string) => {
    return ['FSIP', 'ASIP'].includes(type) ? PaymentTypes.SIP : PaymentTypes.Lumpsum;
};

export const getFoliosFromAmc = (
    amcData: Array<portfolioDetailsType>,
    currentAmc: string,
    filters: Array<Function> = [],
) => {
    const _defaultValues = { folios: [], schemes: [] };
    if (!currentAmc) return _defaultValues;
    const filteredFolioSchemes = amcData
        .filter((amc: portfolioDetailsType) => {
            if (amc.summary['non_demat']?.amc === currentAmc) {
                return amc.schemes;
            }
        })[0]
        ?.schemes.reduce((acc: any, scheme: SCHEMES_RESPONSE) => {
            let valid = filters.every((filter) => filter.call({}, scheme));
            if (valid && scheme.folio && !acc.folios.includes(scheme.folio.toString())) {
                return {
                    folios: [...acc.folios, `${scheme.folio}`],
                    schemes: [...acc.schemes, scheme],
                };
            }
            return acc;
        }, _defaultValues);
    return filteredFolioSchemes;
};
export const shouldAllowDecimal = (value: string, redeemOrSwitchBy?: string) => {
    const decimalPartLength = value.split('.')[1]?.length;
    if (!!decimalPartLength && decimalPartLength > (redeemOrSwitchBy === SwitchBy.Amount ? 2 : 4))
        return false;
    return true;
};

export const isFatcaVerified = (fatcaStatusData: Array<rtaType>, rtaName: rtaNames) => {
    const rta_response = fatcaStatusData.find(
        (rta: any) => rta?.rtaName === rtaName,
    )?.fhFatcaStatus;
    if (rta_response === IsAllow.YES) {
        return true;
    }
    return false;
};

export const portfolioHasIsin = (portfolio: Array<portfolioDetailsType>, isin: string) => {
    let found = false;
    portfolio.forEach((item: portfolioDetailsType) => {
        item.schemes.forEach((scheme) => {
            if (scheme.isin === isin) found = true;
        });
    });
    return found;
};

export const isFolioNew = (portfolio: Array<portfolioDetailsType>, selectedFolio: string) => {
    let currentFolio: any = null;
    portfolio.forEach((item: portfolioDetailsType) => {
        item.schemes.forEach((scheme) => {
            if (String(scheme.folio) === String(selectedFolio)) {
                currentFolio = scheme;
            }
        });
    });
    if (!!currentFolio) {
        return currentFolio.newFolio === IsAllow.YES;
    }
    return false;
};

export const getValueForNewPurchase = (
    purchaseType: folioPurchaseEnum,
    isin: string,
    portfolioData: Array<portfolioDetailsType>,
    key: keyof AmountType,
    newPurchaseDetails: AmountType | null,
    addPurchaseDetails: AmountType | null,
    currentFolio: string,
) => {
    const folioNotNew = !isFolioNew(portfolioData, currentFolio);
    if (
        purchaseType == folioPurchaseEnum.EXISTING &&
        portfolioHasIsin(portfolioData, isin) &&
        folioNotNew
    ) {
        return !!addPurchaseDetails && addPurchaseDetails[key]
            ? addPurchaseDetails[key]
            : key === 'maxAmount'
                ? DEFAULT_MAX_INVESTMENT
                : 1;
    }
    return !!newPurchaseDetails && newPurchaseDetails[key]
        ? newPurchaseDetails[key]
        : key === 'maxAmount'
            ? DEFAULT_MAX_INVESTMENT
            : 1;
};
export const getValueForAddPurchase = (
    portfolioData: Array<portfolioDetailsType>,
    key: keyof AmountType,
    newPurchaseDetails: AmountType | null,
    addPurchaseDetails: AmountType | null,
    currentFolio: string,
) => {
    const folioNotNew = !isFolioNew(portfolioData, currentFolio);
    if (folioNotNew) {
        return !!addPurchaseDetails && addPurchaseDetails[key]
            ? addPurchaseDetails[key]
            : key === 'maxAmount'
                ? DEFAULT_MAX_INVESTMENT
                : 1;
    }
    return !!newPurchaseDetails && newPurchaseDetails[key]
        ? newPurchaseDetails[key]
        : key === 'maxAmount'
            ? DEFAULT_MAX_INVESTMENT
            : 1;
};

export function $wait(time: number) {
    return new Promise((res, rej) => {
        setTimeout(() => {
            res('over');
        }, time);
    });
}

export const TRXN_TYPE_MAP = {
    additionalPurchase: 'AP',
    additionalPurchaseSip: 'SIP',
    newPurchase: 'FP',
    redemption: 'R',
    switch: 'S',
    prospect: 'Prospect',
    stp: 'STP',
    swp: 'SWP',
    sipPause: 'SIPP',
    sipCancel: 'SIPC',
} as const;

export const NFT_TRXN_TYPE_MAP = {
    changeOfIdcw: 'COD',
    unregisteredFolios: 'COC',
    changeEmail: 'COC',
    changeMobile: 'COC',
    consolidationOfFolios: 'COFI',
    correctionOfErroneousData: 'DE',
    changeBankDetails: 'COB',
    multipleBankMandate: 'MBMR',
    updateNomineeDetails: 'NOM',
    updateBankIFSC: 'COI',
    changeOfTaxStatus: 'COT',
    transmissionRequest: 'TRR',
    fatcaCrsDeclaration: 'FAT',
    minorToMajor: 'MAM',
    updateForm15: 'F15GH',
    updatePan: 'CPAN',
} as const;

export type NFT_TRXN_MAP_TYPE = typeof NFT_TRXN_TYPE_MAP;
export type NFT_TRXN_MAP_TYPE_KEYS = keyof NFT_TRXN_MAP_TYPE;
export type NFT_TRXN_MAP_TYPE_VALUES = NFT_TRXN_MAP_TYPE[NFT_TRXN_MAP_TYPE_KEYS];

export type TRXN_MAP_TYPE = typeof TRXN_TYPE_MAP;
export type TRXN_MAP_TYPE_KEYS = keyof TRXN_MAP_TYPE;
export type TRXN_MAP_TYPE_VALUES = TRXN_MAP_TYPE[TRXN_MAP_TYPE_KEYS];
